/**
 * SkillsTab (에이전트 변환) — 에이전트가 변환 스크립트를 실행/수정/검증.
 *
 * 포함:
 *  - 테넌트 / BSDA 선택
 *  - PROMPT 파이프라인 (converter / fail_fix / validate_fix / fix_loop)
 *  - 선택 배치 실행
 *  - 변환 히스토리 테이블 (success/failed/no_file · prompt · file · 건수)
 *  - 수동 변환 실행 (prompt_id 지정)
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";

import * as skillsApi from "../api/skills";
import * as tenantsApi from "../api/tenants";
import { CompareSubTab } from "./CompareSubTab";
import type {
  Agent,
  AgentStatus,
  ConverterHistoryEntry,
  ConverterPromptSummary,
  ConverterCalendarDay,
  FixLoopPromptStatus,
  SkillHistoryEntry,
} from "../api/types";
import { buildPromptPipeline, summarizePipeline, yesterdayBsda, type PromptRow } from "../helpers/pipeline";
import { formatTimestamp } from "../helpers/format";
import type { ValidationSummary } from "../api/skills";
import { PipelineTable, type ActionType } from "./PipelineTable";

const STATUS_COLOR: Record<string, "success" | "error" | "warning" | "default"> = {
  success: "success",
  failed: "error",
  no_file: "warning",
};

// Phase-2 monitoring enrichments. Default off in production.
// REACT_APP_FEATURE_MONITORING_V2=true 빌드 시 활성화. 신규 endpoint 5xx 시 자동 fallback.
const FEATURE_MONITORING_V2 =
  process.env.REACT_APP_FEATURE_MONITORING_V2 === "true";

/** 30일 mini heatmap. 셀 클릭 시 onPick 호출. */
function MiniHeatmap({
  days,
  selectedBsda,
  onPick,
}: {
  days: ConverterCalendarDay[];
  selectedBsda: string;
  onPick: (bsda: string) => void;
}) {
  if (days.length === 0) return null;
  const cellColor = (d: ConverterCalendarDay): string => {
    if (d.total === 0) return "#f0f0f0";
    if (d.failed === 0 && d.success > 0) return "#a5d6a7"; // green
    if (d.success === 0 && d.failed > 0) return "#ef9a9a"; // red
    if (d.success === 0 && d.failed === 0) return "#e0e0e0"; // gray (no_file)
    return "#fff59d"; // yellow (mixed)
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary">최근 30일</Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${days.length}, 14px)`,
          gap: "2px",
          p: 0.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        {days.map((d) => {
          const isSelected = d.bsda === selectedBsda;
          return (
            <Tooltip
              key={d.bsda}
              title={`${d.bsda} · 성공 ${d.success} / 실패 ${d.failed} / 파일없음 ${d.no_file}`}
            >
              <Box
                component="button"
                onClick={() => onPick(d.bsda)}
                sx={{
                  width: 14,
                  height: 14,
                  bgcolor: cellColor(d),
                  border: isSelected ? "2px solid #1976d2" : "1px solid transparent",
                  borderRadius: "2px",
                  p: 0,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

export function PipelineSubTab({
  initialTenantId,
  initialBsda,
}: {
  initialTenantId?: string;
  initialBsda?: string;
} = {}) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [tenant, setTenant] = useState<string>(initialTenantId || "");
  const [bsda, setBsda] = useState<string>(initialBsda || yesterdayBsda());
  const [history, setHistory] = useState<ConverterHistoryEntry[]>([]);
  const [skillHistory, setSkillHistory] = useState<SkillHistoryEntry[]>([]);
  const [fixLoop, setFixLoop] = useState<FixLoopPromptStatus[]>([]);
  const [prompts, setPrompts] = useState<string[]>([]);
  const [promptId, setPromptId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [runningSkills, setRunningSkills] = useState<Set<string>>(new Set());
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState<{ current: number; total: number } | null>(null);
  const [validation, setValidation] = useState<ValidationSummary | null>(null);
  // Phase-2 enrichments. 기본 빈 상태 — feature flag off 또는 fetch 실패 시 그대로 둠.
  const [summaryByPrompt, setSummaryByPrompt] = useState<Map<string, ConverterPromptSummary>>(new Map());
  const [calendar, setCalendar] = useState<ConverterCalendarDay[]>([]);

  useEffect(() => {
    tenantsApi
      .fetchTenants()
      .then((list) => {
        setAgents(list);
        if (list.length > 0) setTenant(list[0].tenantCode);
      })
      .catch(() => setAgents([]));
  }, []);

  const loadHistory = useCallback(
    async (t: string, b: string) => {
      if (!t) return;
      setLoading(true);
      try {
        const today = new Date();
        const past = new Date(today.getTime() - 29 * 24 * 60 * 60 * 1000);
        const fmt = (d: Date) => d.toISOString().slice(0, 10).replace(/-/g, "");

        const [cvt, skl, fxl, val, sum, cal] = await Promise.allSettled([
          skillsApi.fetchConverterHistory(t, { bsda: b, limit: 100 }),
          skillsApi.fetchSkillHistory(t, { limit: 100 }),
          skillsApi.fetchFixLoopAttempts(t, b),
          skillsApi.fetchValidationSummary(t, b),
          // Phase-2 신규 — feature flag off 시 reject 로 처리되어 fallback.
          FEATURE_MONITORING_V2
            ? skillsApi.fetchConverterSummary(t, 30)
            : Promise.reject(new Error("feature off")),
          FEATURE_MONITORING_V2
            ? skillsApi.fetchConverterCalendar(t, fmt(past), fmt(today))
            : Promise.reject(new Error("feature off")),
        ]);
        setHistory(cvt.status === "fulfilled" ? cvt.value.runs : []);
        setSkillHistory(skl.status === "fulfilled" ? skl.value.runs : []);
        setFixLoop(fxl.status === "fulfilled" ? fxl.value.prompts : []);
        setValidation(val.status === "fulfilled" ? val.value : null);
        if (sum.status === "fulfilled") {
          const m = new Map<string, ConverterPromptSummary>();
          for (const p of sum.value.prompts) m.set(p.prompt_id, p);
          setSummaryByPrompt(m);
        } else {
          setSummaryByPrompt(new Map());
        }
        setCalendar(cal.status === "fulfilled" ? cal.value.days : []);
        setError(null);
      } catch (e) {
        setError(
          e instanceof Error ? e.message : "변환 히스토리를 불러오지 못했습니다.",
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const loadPrompts = useCallback(
    async (t: string) => {
      if (!t) return;
      try {
        const data = await skillsApi.fetchConverterPrompts(t);
        setPrompts(data.prompts);
        setPromptId((prev) => (prev || data.prompts[0] || ""));
      } catch {
        setPrompts([]);
      }
    },
    [],
  );

  useEffect(() => {
    if (tenant) {
      loadHistory(tenant, bsda);
      loadPrompts(tenant);
    }
  }, [tenant, bsda, loadHistory, loadPrompts]);

  const runConverter = async () => {
    if (!tenant || !promptId) return;
    setRunning(true);
    try {
      await skillsApi.runConverter(tenant, { prompt_id: promptId, bsda });
      setError(null);
      await loadHistory(tenant, bsda);
    } catch (e) {
      setError(e instanceof Error ? `변환 실행 실패: ${e.message}` : "변환 실행에 실패했습니다.");
    } finally {
      setRunning(false);
    }
  };

  const runConverterFor = async (pid: string) => {
    if (!tenant) return;
    setRunning(true);
    try {
      await skillsApi.runConverter(tenant, { prompt_id: pid, bsda });
      await loadHistory(tenant, bsda);
    } catch (e) {
      setError(
        e instanceof Error ? `컨버터 실행 실패: ${e.message}` : "컨버터 실행 실패",
      );
    } finally {
      setRunning(false);
    }
  };

  const runSkillFor = async (
    skill: "fail_fix" | "validate_fix",
    pid: string,
  ) => {
    if (!tenant) return;
    const key = `${skill}:${pid}`;
    setRunningSkills((prev) => new Set(prev).add(key));
    try {
      await skillsApi.runSkill(tenant, skill, { bsda });
      await loadHistory(tenant, bsda);
    } catch (e) {
      setError(e instanceof Error ? `${skill} 실행 실패: ${e.message}` : `${skill} 실행 실패`);
    } finally {
      setRunningSkills((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const runBatch = async (type: ActionType) => {
    if (!tenant || selected.size === 0) return;
    const ids = Array.from(selected);
    setBatchRunning(true);
    setBatchProgress({ current: 0, total: ids.length });
    try {
      for (let i = 0; i < ids.length; i++) {
        const pid = ids[i];
        setBatchProgress({ current: i + 1, total: ids.length });
        try {
          if (type === "converter") {
            await skillsApi.runConverter(tenant, { prompt_id: pid, bsda });
          } else {
            await skillsApi.runSkill(tenant, type, { bsda });
          }
        } catch {
          // 개별 실패는 무시하고 계속
        }
      }
      await loadHistory(tenant, bsda);
      setSelected(new Set());
    } finally {
      setBatchRunning(false);
      setBatchProgress(null);
    }
  };

  const togglePrompt = (pid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const toggleAllPrompts = () => {
    setSelected((prev) => {
      if (prev.size === prompts.length) return new Set();
      return new Set(prompts);
    });
  };

  const pipeline: PromptRow[] = useMemo(
    () =>
      buildPromptPipeline({
        prompts,
        converterHistory: history,
        skillHistory,
        fixLoop,
      }),
    [prompts, history, skillHistory, fixLoop],
  );

  const summary = useMemo(() => {
    const s = { success: 0, failed: 0, no_file: 0 };
    history.forEach((h) => {
      if (h.status in s) s[h.status as keyof typeof s] += 1;
    });
    return s;
  }, [history]);

  const runningTenant = agents.find((a) => a.tenantCode === tenant);
  const needsStart = runningTenant && (runningTenant.status as AgentStatus) !== "Running";

  return (
    <Stack spacing={2}>
      {/* 컨트롤 바 */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
          <Select
            size="small"
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            sx={{ minWidth: 220 }}
            displayEmpty
          >
            {agents.length === 0 && <MenuItem value="">테넌트 없음</MenuItem>}
            {agents.map((a) => (
              <MenuItem key={a.tenantCode} value={a.tenantCode}>
                {a.emoji} {a.nameKorean}
              </MenuItem>
            ))}
          </Select>

          <TextField
            size="small"
            label="BSDA"
            value={bsda}
            onChange={(e) => setBsda(e.target.value.replace(/\D/g, "").slice(0, 8))}
            placeholder="YYYYMMDD"
            helperText="8자리 날짜"
            sx={{ width: 160 }}
          />

          <Select
            size="small"
            value={promptId}
            onChange={(e) => setPromptId(e.target.value)}
            sx={{ minWidth: 220, flex: 1 }}
            displayEmpty
          >
            {prompts.length === 0 && <MenuItem value="">프롬프트 없음</MenuItem>}
            {prompts.map((p) => (
              <MenuItem key={p} value={p}>{p}</MenuItem>
            ))}
          </Select>

          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
            onClick={runConverter}
            disabled={!tenant || !promptId || running}
          >
            변환 실행
          </Button>

          <Tooltip title="새로고침">
            <IconButton onClick={() => loadHistory(tenant, bsda)} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {FEATURE_MONITORING_V2 && (
            <MiniHeatmap days={calendar} selectedBsda={bsda} onPick={setBsda} />
          )}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} data-testid="tab-summary-chips">
          <Chip size="small" label={`성공 ${summary.success}`} color={summary.success > 0 ? "success" : "default"} />
          <Chip size="small" label={`실패 ${summary.failed}`} color={summary.failed > 0 ? "error" : "default"} />
          <Chip size="small" label={`파일없음 ${summary.no_file}`} color={summary.no_file > 0 ? "warning" : "default"} />
          <Chip size="small" label={`전체 ${history.length}`} variant="outlined" />
        </Stack>
      </Paper>

      {needsStart && (
        <Alert severity="info">
          선택한 테넌트의 에이전트가 정지 상태입니다. 에이전트 변환은 에이전트가 Running이어야 동작합니다.
          에이전트 관리 탭에서 Start 후 다시 시도하세요.
        </Alert>
      )}

      {error && <Alert severity="warning">{error}</Alert>}

      {/* BSDA 요약 배너 — 선택한 BSDA 기준 전체 흐름을 한눈에 */}
      {prompts.length > 0 && (() => {
        const s = summarizePipeline(pipeline);
        const bsdaLabel = bsda.length === 8 ? `${bsda.slice(0,4)}-${bsda.slice(4,6)}-${bsda.slice(6,8)}` : bsda;
        const isToday = bsda === yesterdayBsda().replace(/-/g, "").slice(0,8) ? false : (() => {
          const d = new Date();
          const kstY = d.getFullYear(), kstM = String(d.getMonth()+1).padStart(2,"0"), kstD = String(d.getDate()).padStart(2,"0");
          return bsda === `${kstY}${kstM}${kstD}`;
        })();
        return (
          <Alert
            severity={s.failed > 0 ? "warning" : s.inProgress > 0 ? "info" : "success"}
            icon={false}
            sx={{ "& .MuiAlert-message": { width: "100%" } }}
          >
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 200 }}>
                📅 {bsdaLabel} {isToday ? "(오늘)" : "(과거)"} · {s.total}개 PROMPT
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" color="success" variant="outlined" label={`✓ 완료 ${s.done}`} />
                {s.inProgress > 0 && <Chip size="small" color="info" variant="outlined" label={`⏳ 진행중 ${s.inProgress}`} />}
                {s.failed > 0 && <Chip size="small" color="error" variant="outlined" label={`✗ 실패 ${s.failed}`} />}
                {s.noFile > 0 && <Chip size="small" color="warning" variant="outlined" label={`📄 파일없음 ${s.noFile}`} />}
                {s.notRun > 0 && <Chip size="small" variant="outlined" label={`· 미실행 ${s.notRun}`} />}
                {validation && validation.total > 0 && (
                  <Tooltip title={`ralph_validate 검증: 컬럼 일치 / 중복 없음 / 날짜 완전성 / 레코드 건수 4개 check 모두 PASS 한 prompt 수`}>
                    <Chip
                      size="small"
                      color={validation.fail > 0 ? "error" : "success"}
                      variant="outlined"
                      label={`🧪 검증통과 ${validation.pass}/${validation.total}`}
                    />
                  </Tooltip>
                )}
                {validation && validation.column_mismatch > 0 && (
                  <Tooltip title="기대 컬럼 vs 실제 HAI 컬럼 불일치 (각 행 펼쳐서 상세 확인)">
                    <Chip
                      size="small"
                      color="error"
                      variant="filled"
                      label={`⚠ 컬럼불일치 ${validation.column_mismatch}`}
                    />
                  </Tooltip>
                )}
              </Stack>
              {!isToday && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                  ※ 상단 "지금 상태"는 오늘(KST) 기준이라 숫자가 다를 수 있음
                </Typography>
              )}
            </Stack>
          </Alert>
        );
      })()}

      {/* PROMPT 파이프라인 현황 */}
      {prompts.length > 0 && (
        <>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 1 }}>
            PROMPT 파이프라인 현황
          </Typography>
          <PipelineTable
            rows={pipeline}
            loading={loading}
            converterRunning={running}
            runningSkills={runningSkills}
            batchRunning={batchRunning}
            batchProgress={batchProgress}
            selected={selected}
            tenantCode={tenant}
            bsda={bsda}
            onToggle={togglePrompt}
            onToggleAll={toggleAllPrompts}
            onRefresh={() => loadHistory(tenant, bsda)}
            onRunConverter={runConverterFor}
            onRunSkill={runSkillFor}
            onBatchRun={runBatch}
            summaryByPrompt={summaryByPrompt}
            featureV2={FEATURE_MONITORING_V2}
          />
        </>
      )}

      {/* 탭3-c — 원본 vs HAI 결정적 비교 (MONITORING_SPEC Phase 5). */}
      {tenant && promptId && /^\d{8}$/.test(bsda) && (
        <Accordion variant="outlined" sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" fontWeight={600}>
              원본 ↔ HAI 비교 (CLI `aify compare conversion` 의 web 화)
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CompareSubTab
              tenantCode={tenant}
              bsda={bsda}
              promptId={promptId}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2 }}>
        변환 히스토리
      </Typography>

      {/* 히스토리 테이블 */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : history.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            해당 BSDA의 변환 기록이 없습니다.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>상태</TableCell>
                <TableCell>Prompt</TableCell>
                <TableCell>File</TableCell>
                <TableCell align="right">Records</TableCell>
                <TableCell>시작</TableCell>
                <TableCell>종료</TableCell>
                <TableCell>Trigger</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((h, idx) => (
                <TableRow key={`${h.prompt_id}-${h.file_id ?? ""}-${h.started_at}-${idx}`} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      label={h.status}
                      color={STATUS_COLOR[h.status] || "default"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace" }}>
                      {h.prompt_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                      {h.file_id || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="caption">{h.record_count ?? "—"}</Typography>
                  </TableCell>
                  <TableCell><Typography variant="caption">{formatTimestamp(h.started_at)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{formatTimestamp(h.completed_at)}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{h.triggered_by || "—"}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
